import { useState, useEffect } from 'react'
import { Calculator, X } from 'lucide-react'

interface EstimateItem {
  name: string
  unit: string
  quantity: number
  unitPrice: number
  amount: number
}

interface EstimateModalProps {
  canvasData: any
  onClose: () => void
}

export const EstimateModal = ({ canvasData, onClose }: EstimateModalProps) => {
  const [items, setItems] = useState<EstimateItem[]>([])
  const [total, setTotal] = useState(0)

  useEffect(() => {
    calculateEstimate()
  }, [canvasData])

  const calculateEstimate = () => {
    if (!canvasData) return

    const estimateItems: EstimateItem[] = []
    
    // 部品をカウント
    const components: { [key: string]: number } = {}
    const wires: { [key: string]: { length: number; pipeSize: string; cableType: string } } = {}
    
    canvasData.getObjects().forEach((obj: any) => {
      // 部品のカウント
      if (obj.componentType) {
        components[obj.componentType] = (components[obj.componentType] || 0) + 1
      }
      
      // 配線のカウント
      if (obj.wireData) {
        const key = `${obj.wireData.wireType}_${obj.wireData.pipeSize}_${obj.wireData.cableType}`
        if (!wires[key]) {
          wires[key] = {
            length: 0,
            pipeSize: obj.wireData.pipeSize,
            cableType: obj.wireData.cableType
          }
        }
        wires[key].length += obj.wireData.length
      }
    })
    
    // 部品の単価マスタ（デモ用）
    const componentPrices: { [key: string]: { name: string; unit: string; price: number } } = {
      charger: { name: 'EV充電器', unit: '台', price: 350000 },
      panel: { name: '分電盤', unit: '面', price: 80000 },
      breaker: { name: 'ブレーカー', unit: '個', price: 15000 },
      pullbox: { name: 'プルボックス', unit: '個', price: 3000 },
      signboard: { name: '案内板', unit: '枚', price: 25000 },
      pole: { name: '電柱', unit: '本', price: 150000 },
    }
    
    // 配線の単価マスタ（デモ用：m単価）
    const wirePrices: { [key: string]: { pipeName: string; cableName: string; pipePrice: number; cablePrice: number } } = {
      'CD 16': { pipeName: 'CD管 Φ16', cableName: '', pipePrice: 800, cablePrice: 0 },
      'CD 22': { pipeName: 'CD管 Φ22', cableName: '', pipePrice: 1200, cablePrice: 0 },
      'CD 28': { pipeName: 'CD管 Φ28', cableName: '', pipePrice: 1500, cablePrice: 0 },
      'CV 5.5sq-3C': { pipeName: '', cableName: 'CVケーブル 5.5sq-3C', pipePrice: 0, cablePrice: 1500 },
      'CV 8sq-3C': { pipeName: '', cableName: 'CVケーブル 8sq-3C', pipePrice: 0, cablePrice: 2200 },
      'CV 14sq-3C': { pipeName: '', cableName: 'CVケーブル 14sq-3C', pipePrice: 0, cablePrice: 3500 },
    }
    
    // 部品を見積もりに追加
    Object.entries(components).forEach(([type, count]) => {
      const priceInfo = componentPrices[type]
      if (priceInfo) {
        estimateItems.push({
          name: priceInfo.name,
          unit: priceInfo.unit,
          quantity: count,
          unitPrice: priceInfo.price,
          amount: count * priceInfo.price
        })
      }
    })
    
    // 配線を見積もりに追加
    Object.entries(wires).forEach(([_, data]) => {
      const length = Math.ceil(data.length) // 切り上げ
      
      // 配管
      const pipePrice = wirePrices[data.pipeSize]?.pipePrice || 1000
      estimateItems.push({
        name: `配管 ${data.pipeSize}`,
        unit: 'm',
        quantity: length,
        unitPrice: pipePrice,
        amount: length * pipePrice
      })
      
      // ケーブル
      const cablePrice = wirePrices[data.cableType]?.cablePrice || 1500
      estimateItems.push({
        name: `ケーブル ${data.cableType}`,
        unit: 'm',
        quantity: length,
        unitPrice: cablePrice,
        amount: length * cablePrice
      })
    })
    
    // 工事費（デモ用：部品数 × 20000円）
    const installCount = Object.values(components).reduce((sum, count) => sum + count, 0)
    estimateItems.push({
      name: '設置工事費',
      unit: '式',
      quantity: 1,
      unitPrice: installCount * 20000,
      amount: installCount * 20000
    })
    
    setItems(estimateItems)
    setTotal(estimateItems.reduce((sum, item) => sum + item.amount, 0))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Calculator size={24} className="text-blue-600" />
            見積もり（デモ版）
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>
        
        {/* 見積もり表 */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">品名</th>
                <th className="border p-2 text-center">数量</th>
                <th className="border p-2 text-center">単位</th>
                <th className="border p-2 text-right">単価</th>
                <th className="border p-2 text-right">金額</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border p-2">{item.name}</td>
                  <td className="border p-2 text-center">{item.quantity.toFixed(1)}</td>
                  <td className="border p-2 text-center">{item.unit}</td>
                  <td className="border p-2 text-right">¥{item.unitPrice.toLocaleString()}</td>
                  <td className="border p-2 text-right font-semibold">
                    ¥{item.amount.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50 font-bold">
                <td colSpan={4} className="border p-3 text-right">合計金額（税抜）</td>
                <td className="border p-3 text-right text-lg text-blue-600">
                  ¥{total.toLocaleString()}
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan={4} className="border p-2 text-right">消費税（10%）</td>
                <td className="border p-2 text-right">
                  ¥{Math.floor(total * 0.1).toLocaleString()}
                </td>
              </tr>
              <tr className="bg-green-50 font-bold text-lg">
                <td colSpan={4} className="border p-3 text-right">合計金額（税込）</td>
                <td className="border p-3 text-right text-green-600">
                  ¥{Math.floor(total * 1.1).toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
          
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-800">
              <strong>※ デモ版の注意事項:</strong>
            </p>
            <ul className="text-sm text-yellow-700 mt-2 list-disc list-inside">
              <li>単価は仮の値です。実際の単価マスタと連携する必要があります。</li>
              <li>配線長は図面上の直線距離で計算しています。</li>
              <li>工事費は簡易計算です（部品数 × 2万円）。</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
